import { getIndustry } from './industryConfig';
// FinlyBooks export utilities — PDF (print) and Excel (CSV) for all reports

export function exportToCSV(filename, headers, rows) {
  const csv = [headers, ...rows]
    .map(r => r.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','))
    .join('\n');
  const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename.endsWith('.csv') ? filename : filename + '.csv';
  a.click();
  URL.revokeObjectURL(url);
}

// Opens a print-ready window styled as a PDF. User can "Save as PDF".
export function exportToPDF(title, businessName, bodyHTML, periodLabel = '') {
  const win = window.open('', '_blank');
  if (!win) { alert('Please allow pop-ups to export PDF'); return; }
  win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>${title}</title>
  <style>
    *{margin:0;padding:0;box-sizing:border-box}
    body{font-family:Arial,Helvetica,sans-serif;padding:40px;color:#222}
    .header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:28px;padding-bottom:16px;border-bottom:2px solid #0f6e56}
    .brand{color:#0f6e56;font-size:26px;font-weight:700;font-family:Georgia,serif}
    .sub{color:#888;font-size:13px;margin-top:4px}
    .rtype{font-size:15px;color:#222;font-weight:600;margin-top:6px}
    .period{font-size:12px;color:#888;text-align:right}
    table{width:100%;border-collapse:collapse;font-size:13px;margin-bottom:20px}
    th{background:#f7f8fa;padding:10px 14px;text-align:left;border-bottom:2px solid #e8e8e8;font-size:11px;text-transform:uppercase;letter-spacing:0.3px;color:#666}
    td{padding:9px 14px;border-bottom:1px solid #f0f0f0}
    .num{text-align:right}
    .total td{font-weight:700;background:#f7f8fa;font-size:14px}
    .green{color:#0f6e56}.red{color:#a32d2d}
    .section-title{font-size:14px;font-weight:700;color:#0f6e56;margin:18px 0 8px;padding:6px 0;border-left:3px solid #0f6e56;padding-left:10px}
    .footer{margin-top:36px;text-align:center;color:#aaa;font-size:11px;border-top:1px solid #e8e8e8;padding-top:14px}
    @media print{body{padding:20px}.no-print{display:none}}
  </style></head><body>
  <div class="header">
    <div>
      <div class="brand">${businessName || 'FinlyBooks'}</div>
      <div class="rtype">${title}</div>
    </div>
    <div class="period">${periodLabel ? 'Period: ' + periodLabel + '<br>' : ''}Generated: ${new Date().toLocaleDateString()}</div>
  </div>
  ${bodyHTML}
  <div class="footer">${businessName || 'FinlyBooks'} · Powered by FinlyBooks · Track. Manage. Grow.<br>www.finlybooks.com · © 2025 FinlyBooks</div>
  <div class="no-print" style="text-align:center;margin-top:24px">
    <button onclick="window.print()" style="padding:12px 28px;background:#0f6e56;color:#fff;border:none;border-radius:8px;font-size:14px;font-weight:600;cursor:pointer">🖨️ Print / Save as PDF</button>
  </div>
  </body></html>`);
  win.document.close();
  setTimeout(() => { win.focus(); }, 300);
}

export const fmt = n => '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Build an invoice PDF for sharing (travel-agency aware, logo-aware)
export function exportInvoicePDF(invoice, business, party, currencyMode) {
  const issales = invoice.type === 'sales';
  const baseCur = (business && business.currency) ? business.currency : 'USD';
  const isForeign = invoice.currency && invoice.currency !== baseCur;
  // Decide which currency to render: explicit mode, else the invoice's own currency
  const mode = currencyMode || (isForeign ? 'foreign' : 'base');
  const showForeign = isForeign && mode === 'foreign';
  const cur = (showForeign ? invoice.currency : baseCur) + ' ';
  const money = n => cur + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fare = showForeign ? (Number(invoice.fareForeign) || 0) : (Number(invoice.fareAmount != null ? invoice.fareAmount : invoice.amount) || 0);
  const disc = showForeign ? (Number(invoice.discountForeign) || 0) : (Number(invoice.discountAmount) || 0);
  const tax = showForeign ? (Number(invoice.taxForeign) || 0) : (Number(invoice.taxAmount) || 0);
  const net = showForeign ? (Number(invoice.netForeign) || 0) : (Number(invoice.amount) || (fare - disc + tax));
  // secondary line showing the other currency for reference
  const altLine = isForeign ? (showForeign
      ? `In ${baseCur}: ${baseCur} ${(Number(invoice.amount)||0).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})} @ ${invoice.fxRate}`
      : `Invoice currency ${invoice.currency}: ${invoice.currency} ${(Number(invoice.netForeign)||0).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})} @ ${invoice.fxRate}`) : '';

  // Detail rows come from the invoice's industry edition
  const cfg = getIndustry(invoice.industry || (business && business.industry));
  const labelFor = {
    pnr: 'Booking / PNR', paxName: 'Passenger', sector: 'Sector / Route', airline: 'Airline / Supplier',
    refNo: 'Ticket / Doc No.', serviceDate: cfg.id === 'tourism' ? 'Travel Date' : 'Service Date', returnDate: 'Return Date',
    jobName: 'Job / Project', staffName: 'Staff / Consultant', hours: 'Hours / Units', rate: 'Rate',
    itemName: 'Item / Product', qty: 'Quantity',
  };
  const detailRows = cfg.fields
    .filter(fld => fld.key !== 'description' && invoice[fld.key])
    .map(fld => [labelFor[fld.key] || fld.label, invoice[fld.key]]);
  const travelBlock = detailRows.length ? `
    <div style="background:${cfg.accent}0d;border:1px solid ${cfg.accent}33;border-radius:8px;padding:14px 16px;margin-bottom:18px">
      <div style="font-size:11px;font-weight:700;color:${cfg.accent};text-transform:uppercase;letter-spacing:0.5px;margin-bottom:8px">${cfg.detailsTitle}</div>
      <table style="width:100%;font-size:12.5px;border:none">
        ${detailRows.map(r => `<tr><td style="border:none;padding:2px 8px 2px 0;color:#888;width:150px">${r[0]}</td><td style="border:none;padding:2px 0;font-weight:600">${r[1]}</td></tr>`).join('')}
      </table>
    </div>` : '';

  // Logo (base64) if present, else brand wordmark
  const logoHTML = (business && business.logo)
    ? `<img src="${business.logo}" alt="logo" style="max-height:64px;max-width:220px;margin-bottom:8px" />`
    : `<div style="color:#0f6e56;font-size:26px;font-weight:700;font-family:Georgia,serif">${(business && business.businessName) || 'FinlyBooks'}</div>`;

  const contactLine = [business && business.address, business && business.city, business && business.country].filter(Boolean).join(', ');

  const win = window.open('', '_blank');
  if (!win) { alert('Please allow pop-ups to export PDF'); return; }
  win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>${invoice.invoiceNo || 'Invoice'}</title>
  <style>
    *{margin:0;padding:0;box-sizing:border-box}
    body{font-family:Arial,Helvetica,sans-serif;padding:40px;color:#222}
    table{width:100%;border-collapse:collapse}
    .items th{background:#f7f8fa;padding:10px 14px;text-align:left;border-bottom:2px solid #e8e8e8;font-size:11px;text-transform:uppercase;letter-spacing:0.3px;color:#666}
    .items td{padding:10px 14px;border-bottom:1px solid #f0f0f0;font-size:13px}
    .num{text-align:right}
    .green{color:#0f6e56}.red{color:#a32d2d}
    .footer{margin-top:36px;text-align:center;color:#aaa;font-size:11px;border-top:1px solid #e8e8e8;padding-top:14px}
    @media print{body{padding:20px}.no-print{display:none}}
  </style></head><body>
  <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:24px;padding-bottom:16px;border-bottom:2px solid #0f6e56">
    <div>
      ${logoHTML}
      ${contactLine ? `<div style="font-size:12px;color:#777;margin-top:4px">${contactLine}</div>` : ''}
      ${business && business.phone ? `<div style="font-size:12px;color:#777">Tel: ${business.phone}</div>` : ''}
      ${business && business.taxNumber ? `<div style="font-size:12px;color:#777">Tax/TRN: ${business.taxNumber}</div>` : ''}
    </div>
    <div style="text-align:right">
      <div style="font-size:22px;font-weight:700;color:#222">${issales ? 'INVOICE' : 'PURCHASE'}</div>
      <div style="font-size:15px;font-weight:700;color:#0f6e56;margin-top:2px">${invoice.invoiceNo || ''}</div>
      <div style="font-size:12.5px;color:#666;margin-top:6px">Date: ${invoice.date || ''}</div>
      ${invoice.dueDate ? `<div style="font-size:12.5px;color:#666">Due: ${invoice.dueDate}</div>` : ''}
    </div>
  </div>

  <div style="margin-bottom:18px">
    <div style="font-size:11px;color:#888;text-transform:uppercase;letter-spacing:1px">${issales ? 'Bill To' : 'Bill From'}</div>
    <div style="font-size:16px;font-weight:600;margin-top:4px">${invoice.customerName || invoice.supplierName || ''}</div>
    ${party && party.address ? `<div style="font-size:13px;color:#666;margin-top:2px">${party.address}</div>` : ''}
    ${party && party.phone ? `<div style="font-size:13px;color:#666">${party.phone}</div>` : ''}
    ${party && party.email ? `<div style="font-size:13px;color:#666">${party.email}</div>` : ''}
  </div>

  ${travelBlock}

  <table class="items">
    <thead><tr><th>Description</th><th class="num">Amount</th></tr></thead>
    <tbody>
      <tr><td>${invoice.description || (issales ? 'Travel services' : 'Travel services purchased')}${invoice.sector ? ' — ' + invoice.sector : ''}</td><td class="num">${money(fare)}</td></tr>
      ${disc > 0 ? `<tr><td>Less: Discount</td><td class="num red">− ${money(disc)}</td></tr>` : ''}
      ${tax > 0 ? `<tr><td>Add: Tax${invoice.taxRate ? ' (' + invoice.taxRate + '%)' : ''}</td><td class="num">+ ${money(tax)}</td></tr>` : ''}
    </tbody>
    <tfoot><tr><td style="padding:12px 14px;font-weight:700;font-size:15px;border-top:2px solid #e8e8e8">Net Total</td><td class="num green" style="padding:12px 14px;font-weight:700;font-size:16px;border-top:2px solid #e8e8e8">${money(net)}</td></tr></tfoot>
  </table>
  ${altLine ? `<div style="text-align:right;font-size:11px;color:#888;margin-top:6px">${altLine}</div>` : ''}

  <div style="margin-top:30px;font-size:13px;color:#666">Thank you for choosing ${(business && business.businessName) || 'us'}!</div>
  <div class="footer">${(business && business.businessName) || 'FinlyBooks'} · Powered by FinlyBooks · Track. Manage. Grow.<br>www.finlybooks.com</div>
  <div class="no-print" style="text-align:center;margin-top:24px">
    <button onclick="window.print()" style="padding:12px 28px;background:#0f6e56;color:#fff;border:none;border-radius:8px;font-size:14px;font-weight:600;cursor:pointer">🖨️ Print / Save as PDF</button>
  </div>
  </body></html>`);
  win.document.close();
  setTimeout(() => { win.focus(); }, 300);
}

// ============================================================
// BACKUP & ERP EXPORTS
// ============================================================

// Generic file download helper (text content)
function downloadText(filename, text, mime) {
  const blob = new Blob([text], { type: (mime || 'text/plain') + ';charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

// 1) FULL BACKUP — every collection as JSON (restore / migrate)
export function exportBackupJSON(allData, business) {
  const payload = {
    app: 'FinlyBooks',
    format: 'finlybooks-backup-v1',
    exportedAt: new Date().toISOString(),
    company: business || null,
    data: allData, // { invoices, parties, receipts, payments, expenses, pettycash, cashbook, bankbook, journal, ledgers }
  };
  const stamp = new Date().toISOString().slice(0, 10);
  downloadText(`finlybooks_backup_${stamp}.json`, JSON.stringify(payload, null, 2), 'application/json');
}

// Build the daybook: one row per posting leg (universal double-entry format)
// posts come from generatePostings(); returns { headers, rows }
export function buildDaybook(posts) {
  const headers = ['Date', 'Voucher No', 'Voucher Type', 'Ledger', 'Debit', 'Credit', 'Narration'];
  const rows = [];
  posts.forEach(p => {
    const vtype = vchType(p.ref);
    rows.push([p.date || '', p.ref || '', vtype, p.debit, Number(p.amount).toFixed(2), '', p.narration || '']);
    rows.push([p.date || '', p.ref || '', vtype, p.credit, '', Number(p.amount).toFixed(2), p.narration || '']);
  });
  // trailing totals
  const totDr = posts.reduce((s, p) => s + Number(p.amount || 0), 0);
  rows.push(['', '', '', 'TOTAL', totDr.toFixed(2), totDr.toFixed(2), '']);
  return { headers, rows };
}

function vchType(ref) {
  const r = String(ref || '');
  if (r.startsWith('INV')) return 'Sales';
  if (r.startsWith('PUR')) return 'Purchase';
  if (r.startsWith('RCP')) return 'Receipt';
  if (r.startsWith('PMT')) return 'Payment';
  if (r.startsWith('EXP')) return 'Payment';
  if (r.startsWith('JV')) return 'Journal';
  return 'Journal';
}

// Map our ledger group/head to a Tally parent group name
const TALLY_PARENT = {
  'Bank Accounts': 'Bank Accounts', 'Cash in Hand': 'Cash-in-Hand', 'Sundry Debtors': 'Sundry Debtors',
  'Stock / Inventory': 'Stock-in-Hand', 'Fixed Assets': 'Fixed Assets', 'Loans & Advances (Asset)': 'Loans & Advances (Asset)',
  'Sundry Creditors': 'Sundry Creditors', 'Loans (Liability)': 'Loans (Liability)', 'Duties & Taxes': 'Duties & Taxes',
  'Current Liabilities': 'Current Liabilities', 'Capital Account': 'Capital Account', 'Reserves & Surplus': 'Reserves & Surplus',
  'Sales Account': 'Sales Accounts', 'Direct Income': 'Direct Incomes', 'Indirect Income': 'Indirect Incomes',
  'Purchase Account': 'Purchase Accounts', 'Direct Expenses': 'Direct Expenses', 'Indirect Expenses': 'Indirect Expenses',
};
const GROUP_FALLBACK = { ASSET: 'Current Assets', LIABILITY: 'Current Liabilities', EQUITY: 'Capital Account', INCOME: 'Indirect Incomes', EXPENSE: 'Indirect Expenses' };

function xmlEsc(s) { return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;'); }
function tallyDate(d) { const s = String(d || '').slice(0, 10).replace(/-/g, ''); return s.length === 8 ? s : new Date().toISOString().slice(0, 10).replace(/-/g, ''); }

// 2) TALLY XML — ledger masters + vouchers (full accounting)
// ledgerDefs: array of { name, head, group } (DEFAULT_LEDGERS + custom + party-derived)
export function exportTallyXML(posts, ledgerDefs, companyName) {
  const nameToParent = {};
  ledgerDefs.forEach(l => { nameToParent[l.name] = TALLY_PARENT[l.head] || GROUP_FALLBACK[l.group] || 'Suspense A/c'; });

  // Only export ledgers actually used in postings (keeps import balanced & clean)
  const used = new Set();
  posts.forEach(p => { used.add(p.debit); used.add(p.credit); });

  const masters = [...used].map(name => {
    const parent = nameToParent[name] || 'Suspense A/c';
    return `   <TALLYMESSAGE xmlns:UDF="TallyUDF">
    <LEDGER NAME="${xmlEsc(name)}" ACTION="Create">
     <PARENT>${xmlEsc(parent)}</PARENT>
     <ISBILLWISEON>No</ISBILLWISEON>
     <OPENINGBALANCE>0</OPENINGBALANCE>
    </LEDGER>
   </TALLYMESSAGE>`;
  }).join('\n');

  // Group posting legs into vouchers by ref+date so multi-line entries stay together
  const groups = {};
  posts.forEach(p => {
    const key = (p.ref || 'JV') + '|' + (p.date || '');
    if (!groups[key]) groups[key] = { ref: p.ref, date: p.date, legs: [] };
    groups[key].legs.push(p);
  });

  const vouchers = Object.values(groups).map(g => {
    const vt = vchType(g.ref);
    // In Tally XML: debit = negative amount + ISDEEMEDPOSITIVE Yes; credit = positive + No
    const entries = [];
    g.legs.forEach(p => {
      entries.push({ ledger: p.debit, deemedPos: 'Yes', amount: -Number(p.amount) });
      entries.push({ ledger: p.credit, deemedPos: 'No', amount: Number(p.amount) });
    });
    const lines = entries.map(e => `      <ALLLEDGERENTRIES.LIST>
       <LEDGERNAME>${xmlEsc(e.ledger)}</LEDGERNAME>
       <ISDEEMEDPOSITIVE>${e.deemedPos}</ISDEEMEDPOSITIVE>
       <AMOUNT>${e.amount.toFixed(2)}</AMOUNT>
      </ALLLEDGERENTRIES.LIST>`).join('\n');
    const narr = g.legs[0] ? g.legs[0].narration : '';
    return `   <TALLYMESSAGE xmlns:UDF="TallyUDF">
    <VOUCHER VCHTYPE="${vt}" ACTION="Create" OBJVIEW="Accounting Voucher View">
     <DATE>${tallyDate(g.date)}</DATE>
     <EFFECTIVEDATE>${tallyDate(g.date)}</EFFECTIVEDATE>
     <VOUCHERTYPENAME>${vt}</VOUCHERTYPENAME>
     <VOUCHERNUMBER>${xmlEsc(g.ref || '')}</VOUCHERNUMBER>
     <NARRATION>${xmlEsc(narr)}</NARRATION>
${lines}
    </VOUCHER>
   </TALLYMESSAGE>`;
  }).join('\n');

  const xml = `<ENVELOPE>
 <HEADER>
  <TALLYREQUEST>Import Data</TALLYREQUEST>
 </HEADER>
 <BODY>
  <IMPORTDATA>
   <REQUESTDESC>
    <REPORTNAME>All Masters</REPORTNAME>
    <STATICVARIABLES><SVCURRENTCOMPANY>${xmlEsc(companyName || 'FinlyBooks')}</SVCURRENTCOMPANY></STATICVARIABLES>
   </REQUESTDESC>
   <REQUESTDATA>
${masters}
${vouchers}
   </REQUESTDATA>
  </IMPORTDATA>
 </BODY>
</ENVELOPE>`;
  const stamp = new Date().toISOString().slice(0, 10);
  downloadText(`finlybooks_tally_${stamp}.xml`, xml, 'application/xml');
}
